import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { NgRedux } from '@redux/redux-compatibility.service';
import { actionsList, AppState } from 'src/app/shared/redux/store';
import { RevService } from 'src/app/shared/services/rev.service';
import { RevRule, RevDocStatusEnum, RevDoc } from 'src/app/shared/models/rev.model';
import { CatMatch, DataElement, ProcessRunStatusEnum, ReferenceIssue, IgnoredWord, ReferencePhrase, AssociatedImage, AssociatedImageFinding, DrawerDetails, ProcessRunStatusResult, ConsistencyIssuesToUpdateDto } from 'src/app/shared/models/common';
import { SharedService } from 'src/app/shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import Utils from 'src/app/shared/components/utils/utils';
import { BehaviorSubject, Observable, Subject, forkJoin } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ProcessStatusPollingService } from '@shared/services/process-status-polling.service';
import { GrammarSpellingCheckService } from '@shared/services/grammar-spelling-check.service';
import { IntegrationService } from '@shared/services/integration.service';
import { CommonService } from '@shared/services/common.service';
import { AuthService } from '@shared/services/auth.service';
import { Card, ReferenceDocument } from '@shared/models/cat.model';
import { EmbeddedObjectAnnotation } from '@shared/models/embedded-object-annotation';
import { IssuesFactory } from '@shared/components/issues/factory/issues.factory';
import { ConsistencyIssue, ConsistencyIssueClaim, ConsistencyIssueViewModel } from '@shared/components/issues/factory/model/consistency-issue.models';
import { ComplianceIssue } from '@shared/components/issues/factory/model/compliance-issue.models';
import { FairBalanceIssue } from '@shared/components/issues/factory/model/fair-balance-issue.models';
import { Config } from 'src/app/config';
import { getObjectPropValue } from '@shared/utils';
import { ReevalJobService } from '@shared/services/reeval-job.service';
import { ReevalJobTarget } from '@shared/models/reeval-job.model';
import { InfoMessageInitializer } from '@shared/models/infomessage-initializer';
import { ClaimToolElement } from '@shared/models/claim-tool-element.model';
import { ClaimToolComponent } from '@shared/components/claim-tool/claim-tool.component';
import { ClaimToolAnchorState, ClaimToolIssueState, ClaimToolReturnStateService } from '@shared/services/claim-tool-return-state.service';

interface RevClaimToolReturnState {
  hadUnsavedChanges: boolean;
  dirtySw: boolean;
  dirtyConsistencyCheckResultsSw: boolean;
  baseline: Record<string, unknown>;
  localValues: Record<string, unknown>;
  anchors: ClaimToolAnchorState;
  issues: ClaimToolIssueState;
  //catMatchReferenceOriginals: Map<string, string>;
}

@Component({
    selector: 'phrase-rev-rule-details',
    templateUrl: './phrase-rev-rule-details.component.html',
    styleUrls: ['./phrase-rev-rule-details.component.scss'],
    standalone: false
})
export class PhraseRevRuleDetailsComponent implements OnInit, OnDestroy {
  @Input() focusOnOpen: boolean = false;
  @Input() card: Card | undefined;
  @Input() hasNext: boolean = false;
  @Input() hasPrev: boolean = false;
  @Input() referenceDocuments: ReferenceDocument[] = [];

  @Output() onClose = new EventEmitter();
  @Output() onStatusChange = new EventEmitter();
  @Output() onUpdateSpellingIssues = new EventEmitter();
  @Output() showNextRuleEmitter = new EventEmitter();
  @Output() showPrevRuleEmitter = new EventEmitter();
  @Output() showClaimToolEmitter = new EventEmitter();
  @Output() hideRuleAnnotation = new EventEmitter();
  @Output() setDisplayDetailsDialogActionsFlag = new EventEmitter<RevRule>();
  @ViewChildren('assocImagesDivs') assocImagesDivs!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('claimTool') claimToolComponent: QueryList<ClaimToolComponent>;

  showClaimToolDialog = false;
  claimToolElement: ClaimToolElement | null = null;
  private claimToolReturnState: RevClaimToolReturnState | null = null;

  loading: boolean;
  ruleStatusOptions: DataElement[] = [];
  dirtySw: boolean;
  formatMatchDifferences: any;
  formatMatchPhrase: any;
  formatMatchEntry: any;
  highlightedIds: number[] = [];
  wordsToWhitelist: IgnoredWord[] = [];
  processStatusCheckIntervalMillis = 1000;

  dirtyConsistencyCheckResultsSw = false;

  currEmbeddedObjectAnnotation: EmbeddedObjectAnnotation;
  
  showConsistencyIssuePhrases = false;
  hideClaimConsistenciesTable = true;
  detailsDialogLabel: string = 'translate.fairBalanceIssue.details';
  hideFairBalanceIssuesTable = true;
  hideComplianceIssuesTable = true;
  hideReferencesTable: Boolean = new Boolean(true);
  hideFootnotesTable: Boolean = new Boolean(true);
  hideSuggestedReferencesTable: Boolean = new Boolean(true);
  hideAssociatedImages = new BehaviorSubject<boolean>(true);
  hideAssociatedTables = new BehaviorSubject<boolean>(true);
  hideAssociatedTextTable = true;
  drawerDetails: DrawerDetails;
  destroy$ = new Subject();



  showAddContextDropdown : {
    value: boolean
  }= {
    value: false
  };
  // map: reference id -> show add-anchor dropdown boolean
  showAddAnchorForReference: { [refPhraseId: number]: {
    value: boolean
  } } = {};

  refDocToView: ReferenceDocument | null = null;
  disableUserActions: boolean = false;
  
  get curRule(): RevRule {
    return this.ngRedux.getState().curRevRule;
  }

  get curDoc(): RevDoc {
    return this.ngRedux.getState().curRevDoc;
  }

  get editable(): boolean {
    return (
      this.ngRedux.getState().curRevDoc.statusCode == RevDocStatusEnum.UNDERWAY && !this.ngRedux.getState().curRevDoc.isProcessRunning
    );
  }

  get isExpanded(): boolean {
    return this.ngRedux.getState().revDocDetailsExpanded;
  }
  set isExpanded(expanded: boolean) {
    
    this.ngRedux.getState().revDocDetailsExpanded = expanded;
  }

  get isFullMatch(): boolean {
    return this.curRule.revRuleSimilarityEvent === 'FULL_MATCH';
  }

  constructor(
    private ngRedux: NgRedux<AppState>,
    private revService: RevService,
    private sharedService: SharedService,
    private issueFactory: IssuesFactory,
    private translateService: TranslateService,
    private grammarSpellingService: GrammarSpellingCheckService,
    private integrationService: IntegrationService,
    private authService: AuthService,
    private commonService: CommonService,
    private processStatusPollingService: ProcessStatusPollingService,
    private reevalJobService: ReevalJobService,
    private claimToolReturnStateService: ClaimToolReturnStateService
  ) {}
  ngOnDestroy(): void {
    this.flushPendingPageReload();
    this.hideRuleAnnotation.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {

            

      this.sharedService.checkConsistencyCheckResultsPanelClose?.pipe(takeUntil(this.destroy$)).subscribe(() => {

        this.closeConsistencyCheckResultPanels();
      })
    
    if (!this.curRule.fullyFetched) {
      this.loading = true;
      this.revService.getRevRuleSingle(this.curDoc.docId, this.curRule.phraseId, false, false, this.curRule).pipe(takeUntil(this.destroy$))
        .subscribe(
          revRule => {
            this.loading = false;
            let saveSubject = this.curRule.propagateActionFromParent;
            let parentPhraseId = this.curRule.parentPhraseId;
            let isParentActionSelected = this.curRule.isParentActionSelected;
            Object.assign(this.curRule, revRule);
            this.curRule.propagateActionFromParent = saveSubject;
            this.curRule.parentPhraseId = parentPhraseId;
            this.curRule.isParentActionSelected = isParentActionSelected;
            this.initialize();
          },
          error => {
            this.loading = false;
            this.sharedService.showInfoMessage({
              messageKey: 'translate.alert.searchError',
              infoType: 'error'}
            );
          }
        );
    }

    this.initialize();
  }

  private pendingPageReload = false;

  private flushPendingPageReload(): void {
    if (this.pendingPageReload) {
      this.pendingPageReload = false;
      this.ngRedux.dispatch({ type: actionsList.RELOAD_CAT_DOC_PAGE_REQ, data: false });
    }
  }

  private emitClose(): void {
    this.flushPendingPageReload();
    this.onClose.emit();
  }

  private saveFailed = false;

  private completeSave(): void {
    this.loading = false;
    if (this.reevalJobService.isAsyncEnabled()) {
      this.saveNewBaseline();
      this.pendingPageReload = true;
    } else {
      this.onClose.emit();
    }
  }

  get reevalTarget(): ReevalJobTarget {
    return this.curRule && this.curDoc
      ? { kind: 'REV', docId: this.curDoc.docId, elementId: this.curRule.phraseId, isImage: false }
      : null;
  }

  /**
   * Loads the data of a finished background re-evaluation into this dialog and its tile. Unsaved
   * edits are discarded first, after the user confirms.
   */
  onReevalRefresh(): void {
    this.reloadAfterReeval();
  }

  private reloadAfterReeval(): void {
    this.loading = true;
    this.reevalJobService.refreshTile(this.reevalTarget, true).subscribe({
      complete: () => {
        if (this.destroy$.isStopped) {
          return;
        }
        this.loading = false;
        this.initialize(false);
      }
    });
  }

  initialize(saveOriginal: boolean = true) {
    // populate concatSw from text to format words before further processing
    this.formatMatchEntry = this.curRule.formatMatchEntry;
    if (this.curRule.ruleDispWords.length != this.formatMatchEntry.length) {
      if (this.card && this.card.cardId) {
        this.formatMatchEntry = this.formatMatchEntry.filter(entry => entry.mCardId === this.card.cardId);
        const allFormatDescrEmpty = this.formatMatchEntry.every(entry => !entry.formatDescr || entry.formatDescr.trim() === '');
        if (allFormatDescrEmpty) {
          this.formatMatchEntry = [];
        }
      }
      else {
        this.formatMatchEntry = this._getUniqueEntries(this.curRule.formatMatchEntry);
      }
    }

    this.formatMatchPhrase = this.curRule.formatMatchPhrase;
    if (this.curRule.ruleDispWords.length != this.formatMatchPhrase.length) {
      if (this.card && this.card.cardId) {
        this.formatMatchPhrase = this.formatMatchPhrase.filter(entry => entry.mCardId === this.card.cardId);
      }
      else {
        this.formatMatchPhrase = this._getUniqueEntries(this.curRule.formatMatchPhrase);
      }
    }
    this._populateConcatSw(this.formatMatchEntry);
    this._populateConcatSw(this.formatMatchPhrase);

    this.formatMatchDifferences = this.compareTextObjects(this.formatMatchEntry, this.formatMatchPhrase);
    if (saveOriginal) {
      this.curRule.saveOriginal();
    }

    let actions = [];
    if (this.curRule) {
      actions = this.curRule.revRuleActions.map(
        a => (a == 'APPLY_CHANGE') ? 'MARKED_FOR_CHANGES' : a
      );
    }
    // show all actions on old docs
    let isNewRuleDoc = !!this.curRule.revRuleSimilarityEvent;
    if (isNewRuleDoc) {
      this.ruleStatusOptions =
      this.sharedService.getRevRuleUpdStatusOptions().filter(
        s => {
          return actions.indexOf(s.id) >= 0;
        }
      );
    } else {
      this.ruleStatusOptions = this.sharedService.getRevRuleUpdStatusOptions();
    }

    this.setDisplayDetailsDialogActionsFlag.emit(this.curRule);

    if (this.curRule.contextCatPhraseId) {
      this.currentIndex = this.curRule.ruleCatMatches.findIndex(catMatch => catMatch.catPhrase.phraseId == this.curRule.contextCatPhraseId);
      if (this.currentIndex == -1) {
        this.currentIndex = 0;
      }
    }
    const uiConfig = this.ngRedux.getState().uiConfig;
    if (uiConfig) {

      const user_action_buttons_enabled_when_dirty  = getObjectPropValue(uiConfig, 'rule_details_config.user_action_buttons_enabled_when_dirty');
      if (user_action_buttons_enabled_when_dirty != undefined) {

        this.disableUserActions = !user_action_buttons_enabled_when_dirty;
      }
    }
  }

  _populateConcatSw(entries) {
    entries.map((entry, ind) => entry.concatSw = this.curRule.ruleDispWords[ind].concatSw);
  }

  _getUniqueEntries(entries: any[]): any[] {
    const uniqueMap = new Map();
    for (const entry of entries) {
      if (!uniqueMap.has(entry.id)) {
        uniqueMap.set(entry.id, entry);
      }
    }
    return Array.from(uniqueMap.values());
  }

  close() {
    if (this.dirtySw || this.dirtyConsistencyCheckResultsSw || this.hasWhitelistedWords()) {
      let validationStr = this.translateService.instant(
        'translate.cat.discardEditChanges'
      );

      this.sharedService.emitPopupchReceivedEvent({
        title: 'translate.validation.confirm',
        icon: 'exclamation-triangle',
        subTitle: validationStr,
        keep: false,
        approve: () => {
          this.discardChanges();
          this.emitClose();
        },
      });
    } else {
      this.emitClose();
    }
  }

  onRevStatusChange() {
    if (this.ruleCatMatches.length > 0 && this.curRule.ruleUserStatus) {
      this.curRule.contextCatPhraseId = this.ruleCatMatches[this.currentIndex].catPhrase.phraseId;
    } else {
      this.curRule.contextCatPhraseId = null;
    }
    this.onStatusChange.emit();
  }
  onSaveClickedPreProcessing() {

    if (this.curRule.boundingBoxAnchorsToAdd && this.curRule.boundingBoxAnchorsToAdd.length > 0) {
        // call request in callback create payload for consistencyIssuesToUpdate
        // {
        // refDocAnchorId, re
        //  } 
        // 
        this.loading = true;
        this.commonService.createBoundingBoxesAnchors(this.curRule.boundingBoxAnchorsToAdd, this.curDoc.catVersionId, this.curRule).subscribe({
          next: () => {

            this.onSaveClicked();
            

          },
          error: err => {
            // show error stop loading
            this.loading = false;

          }
        }
        );
      } else {
        this.onSaveClicked();
      }
  }
  onSaveClicked() {
    // send request when response is retrieved 
    // call on save clickedProcessing
    this.loading = true;
    this.saveFailed = false;
    const requests: Observable<any>[] = [];
    //check if all anchors are removed
    this.commonService.setRefConsistencyParamIfAllAnchorsRemoved(this.curRule);

    if (this.curRule.anchorsPathsToUpdate != null && this.curRule.anchorsPathsToUpdate.anchors.length > 0) {

      requests.push(this.commonService.updateAnchors(this.ngRedux.getState().curRevDoc.catVersionId, this.curRule));
    }

    if (this.curRule != null && this.curRule.consistencyIssuesToUpdate != null && this.commonService.hasIssuesToUpdate(this.curRule.consistencyIssuesToUpdate)) {
  
        if (this.reevalJobService.isAsyncEnabled()) {
          const target = this.reevalTarget;
          requests.push(this.commonService.startConsistencyIssuesRun(this.curRule.consistencyIssuesToUpdate)
            .pipe(tap((runNum: number) => this.reevalJobService.startJob(runNum, target))));
        } else {
          requests.push(this.updateConsistencyIssuesAndSave(this.curRule.consistencyIssuesToUpdate));
        }
    }
    if (requests.length > 0) {
      forkJoin(requests).subscribe({
        next: () => this.onSaveClickedProcessing(),
        error: () => {
          this.saveFailed = true;
          this.onSaveClickedProcessing();
        }
      });
    } else {
      this.onSaveClickedProcessing();
    }
  }
  
  updateConsistencyIssuesAndSave(consistencyIssuesToUpdate: ConsistencyIssuesToUpdateDto) {
    return this.commonService.startConsistencyIssuesRun(consistencyIssuesToUpdate).pipe(
      switchMap((runNum: number) => this.processStatusPollingService.pollUntilDone(runNum, { until$: this.destroy$ }))
    );
  }
  onSaveClickedProcessing() {
    this.loading = true;
    //if (this.phrase) this.phrase.phraseText = this.editPhraseText;

    if (this.hasWhitelistedWords()) {
      this.processWhitelistedWords().subscribe(
        _ => {
          const docId = this.ngRedux.getState().curRevDoc.docId;
          this.commonService.addAlgRunHead({doc_id: docId, run_type: 'REV_SPELLING_LIGHT'}).subscribe(
            runNum => {
              const words: string[] = this.wordsToWhitelist.map(word => word.word);
              this.grammarSpellingService.updateSpellingLight(runNum, words).subscribe(
                _ => {
                  let timer = setInterval(() => {
                    this.authService.getProcessStatus(runNum).subscribe(
                      (data) => {
                        if (data.status == ProcessRunStatusEnum.FINISHED || data.status == ProcessRunStatusEnum.FAILED) {
                          clearInterval(timer);

                          // TODO refresh data properly
                          this.onUpdateSpellingIssues.emit();
                          
                          if (this.dirtySw || this.dirtyConsistencyCheckResultsSw) {
                            this.saveRuleChanges();
                          } else {
                            this.completeSave();
                          }
                        }
                      },
                      (err) => {
                        clearInterval(timer);
                      }
                    );
                  }, this.processStatusCheckIntervalMillis);
                }
              )
            },
            err => {
              this.loading = false;
            }
          );
        },
        err => {
          this.loading = false;
        }
      );
    } else {
      if (this.dirtySw || this.dirtyConsistencyCheckResultsSw) {
        this.saveRuleChanges();
      } else {
        this.completeSave();
      }
    }
  }

  saveRuleChanges() {
    this.loading = true;

    if (this.curRule.ruleUserStatus || this.curRule.comment){
      this.revService.updRevRule(
              this.ngRedux.getState().curRevDoc,
              [this.curRule],
              this.curRule.revStatusCode,
              this.curRule.contextCatPhraseId
            ).subscribe(
              (data) => {
                this.loading = false;
                this.dirtySw = false;
                this.dirtyConsistencyCheckResultsSw = false;
                this.reloadPageOrDefer();
              },
              (err) => {
                this.loading = false;
              }
            );
    } else {
    
      this.loading = false;
      this.dirtySw = false;
      this.dirtyConsistencyCheckResultsSw = false;
      this.reloadPageOrDefer();
  }
}

  private saveNewBaseline(): void {
    if (!this.saveFailed) {
      const rule = this.curRule;
      rule.consistencyIssuesToUpdate = this.issueFactory.createEmptyIssuesPayloadLike(rule.consistencyIssuesToUpdate);
      rule.anchorsPathsToUpdate = null;
      rule.boundingBoxAnchorsToAdd = [];
      rule.references?.forEach(ref => ref.saveOriginal());
      rule.saveOriginal();
      this.wordsToWhitelist = [];
      this.dirtySw = false;
      this.dirtyConsistencyCheckResultsSw = false;
    }
  }

  private reloadPageOrDefer(): void {
    if (this.reevalJobService.isAsyncEnabled()) {
      this.saveNewBaseline();
      this.pendingPageReload = true;
    } else {
      this.ngRedux.dispatch({
        type: actionsList.RELOAD_CAT_DOC_PAGE_REQ,
        data: false
      });
    }
  }

  hasWhitelistedWords() {
    return this.wordsToWhitelist.length > 0;
  }

  processWhitelistedWords(): Observable<IgnoredWord[]> {
    const catId = this.ngRedux.getState().curRevDoc.catId;
    return this.grammarSpellingService.addCatSpellingCheckIgnoredWords(catId, this.wordsToWhitelist);
  }

  discardChanges() {
    this.curRule.restoreOriginal();
    let fairBalanceIssues: FairBalanceIssue[] = [];
    this.curRule.fairBalanceIssues?.forEach((issue: FairBalanceIssue) => {
      fairBalanceIssues.push(this.issueFactory.createFairBalanceIssue(issue, issue['embeddedObjectAnnotation']));
    });
    this.curRule.fairBalanceIssues = fairBalanceIssues;
    let consistencyIssues: ConsistencyIssue[] = [];
    this.curRule.consistencyIssues?.forEach((consistencyIssue) => {
      
      consistencyIssues.push(this.issueFactory.createConsistencyIssue(consistencyIssue, consistencyIssue['embeddedObjectAnnotation'], false));
    });
    this.curRule.consistencyIssues = consistencyIssues;
      this.curRule.associatedImages?.forEach(associatedImage => {
        
        let consistencyIssues = [];
        associatedImage.hideConsistencyIssuesTable = false;
        associatedImage.consistencyIssues?.forEach(consistencyIssue => {
          consistencyIssues.push(this.issueFactory.createConsistencyIssue(consistencyIssue, consistencyIssue['embeddedObjectAnnotation'], false, consistencyIssue['consistencyIssueAssocImgParent']));
        })
        associatedImage.consistencyIssues = consistencyIssues;
    })
    let complianceIssues: ComplianceIssue[] = [];
    this.curRule.complianceIssues?.forEach((complianceIssue) => {
      
      complianceIssues.push(this.issueFactory.createComplianceIssue(complianceIssue));
    });
    this.curRule.complianceIssues = complianceIssues;
    this.dirtySw = false;
    this.dirtyConsistencyCheckResultsSw = false;
    this.wordsToWhitelist = [];
    this.curRule.grammarSpellingIssues?.forEach(i => {i.isWhitelisted = false; i.isWhitelistedGlobally = false;});
    this.revService.recalculateRuleSections(this.curRule);
  }

  onReportDisplaySwChange(match: CatMatch) {
    this.curRule.ruleCatMatches
      .filter((m) => m.catMatchSeq != match.catMatchSeq)
      .forEach((r) => {
        r.reportDisplaySw = false;
      });
    //match.reportDisplaySw = true;
    this.saveRuleChanges();
  }

  compareTextObjects(original, modified): any {
    // original = this.phrase;
    // modified = this.match;
    const differences = [];
    let previousDiff = null;

    original.forEach((originalItem, index) => {
      const diffJson = {
        text: null,
        ids: []
      };
      const modifiedItem = modified[index];
      if (originalItem.formatDescr !== modifiedItem.formatDescr) {
        diffJson.text = originalItem.text;
        diffJson.ids.push(originalItem.id);

        const originalFormat = originalItem.formatDescr.split(', ');
        const modifiedFormat = modifiedItem.formatDescr.split(', ');

        originalFormat.forEach((originalAttr, attrIndex) => {
          const [originalAttrKey, originalAttrVal] = originalAttr.split(':');
          const [modifiedAttrKey, modifiedAttrVal] = modifiedFormat[attrIndex].split(':');

          if (originalAttrVal !== modifiedAttrVal) {
            diffJson[originalAttrKey] = [originalAttrVal, modifiedAttrVal];
          }
        });
        if (this.isSameDifference(diffJson, previousDiff)) {
          differences[differences.length - 1].text += ' ' + diffJson.text;
          differences[differences.length - 1].ids.push(diffJson.ids[0]);
        } else {
          differences.push(diffJson);
        }
        previousDiff = diffJson;
      }
    });

    return differences;
  }

  getFriendlyDifferenceName(text: string): string {
    return text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  getFriendlyFontName(text: string): string {
    return text.replace(/['{}]/g, '');
  }

  isHighlighted(ids: number[]): boolean {
    return this.highlightedIds.some(id => ids.includes(id));
  }

  highlightDiff(ids: number[]): void {
    this.highlightedIds = ids;
  }

  clearHighlight(): void {
    this.highlightedIds = [];
  }

  isSameDifference(diff1, diff2): boolean {
    const stripText = ({ text, ids, ...obj }: any) => obj;

    const strippedDiff1 = stripText(diff1);
    if (diff2) {
      const strippedDiff2 = stripText(diff2);
      return Utils.areEqual(strippedDiff1, strippedDiff2);
    } else {
      return false;
    }
  }

  getMatchStatusDescr(): string {
    if(this.card) {
      const revCardMatches = this.ngRedux.getState().revCardMatches;
      const match = revCardMatches.find((match: any) => match.phraseId === this.curRule.phraseId && match.mCardId === this.card.cardId);
      return match ? match.matchStatusDescr : this.curRule.revStatusDescr;
    }
    return this.curRule.revStatusDescr;
  }

  getMatchStatusCode(): string {
    let code = this.curRule.revStatusCode;
    /*
    if(this.card) {
      const revCardMatches = this.ngRedux.getState().revCardMatches;
      const match = revCardMatches.find((match: any) => match.phraseId === this.curRule.phraseId && match.mCardId === this.card.cardId);
      code = match ? match.matchStatusCode : this.curRule.revStatusCode;
    }
    
    return code == 'FULL_MATCH' ? 'PASSED' : 'NOT_PASSED_NEW_CONTENT';
    */
   return code;
  }

  currentIndex: number = 0;

  getCurrentMatch(): CatMatch {
    return this.ruleCatMatches[this.currentIndex];
  }

  prevMatch() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.ruleCatMatches.length - 1;
    }
  }

  nextMatch() {
    if (this.currentIndex < this.ruleCatMatches.length - 1) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;
    }
  }

  get ruleCatMatches(): any[] {
    const revCardMatches = this.ngRedux.getState().revCardMatches;
    if(revCardMatches) {
      let cardMatches;
      if(this.card) {
        cardMatches = revCardMatches.filter((match: any) => match.phraseId === this.curRule.phraseId && match.mCardId === this.card.cardId);
      } else {
        cardMatches = revCardMatches.filter((match: any) => match.phraseId === this.curRule.phraseId);
      }
      if(cardMatches && cardMatches.length > 0) {
        console.log(cardMatches);
        let cardMatchPhraseIds = cardMatches.map((match: any) => match.catPhraseId);
        let result = this.curRule.ruleCatMatches.filter((ruleMatch: any) => cardMatchPhraseIds.includes(ruleMatch.catPhrase?.phraseId));
        return result && result.length > 0 ? result : this.curRule.ruleCatMatches;
      }
    }

    return this.curRule.ruleCatMatches || [];
  }

  getCurrentSimilarity(): number {
    return this.ruleCatMatches[this.currentIndex]?.similarityPct || 0;
  }

  getPrevSimilarity(): number {
    return this.ruleCatMatches[this.currentIndex - 1]?.similarityPct || 0;
  }

  getNextSimilarity(): number {
    return this.ruleCatMatches[this.currentIndex + 1]?.similarityPct || 0;
  }

  getReferenceIssue(phraseId: number, refPhraseId: number): any {
    return this.curRule.referenceIssues?.find(
      issue => 
        (issue.catPhraseId === phraseId && issue.refPhraseId === refPhraseId)
    );
  }
  hasReferenceIssue(referencePhrases: ReferencePhrase[], findFootnotes: boolean = false): boolean {
    
    if (this.ruleCatMatches.length == 0) {
      return false;
    }
    if (!findFootnotes) {
      for (let anchorPhrase of this.catMatchAnchorContext) {
        if (this.getMissingCatMatchAnchorContext(anchorPhrase)) {
          return true;
        }
      }
    }
    let currCatPhraseId = this.ruleCatMatches[this.currentIndex]?.catPhrase?.phraseId;
    
    if (this.getMissingReferenceIssue(currCatPhraseId, findFootnotes).length > 0) {
      
      return true;
    }
    if (referencePhrases == undefined) {
        return false;
    }
      // footnotes have reference issues
      for (let refPhrase of referencePhrases) { 

        if (this.getReferenceIssue(currCatPhraseId, refPhrase.refPhraseId)) {
          
          return true;
        }
      }
      return false;
  }
  hasAssociatedImagesIssue(associatedImages: AssociatedImage[], findTables: boolean = false): boolean{

    // check consistency issues
    if (associatedImages) {
      for (let assocImage of associatedImages) {
        if (assocImage.consistencyIssues && assocImage.consistencyIssues.length > 0 ) {
          return true;
        }
      }
  }

    if (this.ruleCatMatches.length == 0) {
      return false;
    }

    let currCatPhraseId = this.ruleCatMatches[this.currentIndex]?.catPhrase?.phraseId;
    let missingAssociateImageFindings = this.getMissingAssocImageFinding(currCatPhraseId, findTables);

    if (missingAssociateImageFindings.length > 0) {
        return true;
    }
    if (associatedImages == undefined) {  
      return false;
    }
    for (let assocImage of associatedImages) {

      let foundAssociatedImage = this.getAssociatedImageFinding(currCatPhraseId, assocImage.assocImageId);

      if (foundAssociatedImage) {
        if (foundAssociatedImage.assocImageFinding !== 'FULL_MATCH') {
          return true; 
        }
      }
      
    }
    return false;

  }
  hasAssociatedTextIssue(): boolean {

    if (this.ruleCatMatches.length == 0) {
      
      return false;
    }

    let catPhraseId = this.ruleCatMatches[this.currentIndex]?.catPhrase?.phraseId;
    let missingAssocTextFinding = this.getMissingAssocTextFinding(catPhraseId);
    
    if (missingAssocTextFinding.length > 0) {
      
      return true;
    }
    if (this.curRule.associatedText == undefined) { 
        return false;
    }

    for (let assocPhrase of this.curRule.associatedText) {
        
      let foundAssociatedTextFinding = this.getAssociatedTextFinding(catPhraseId, assocPhrase.assocPhraseId);
        if (foundAssociatedTextFinding) {
          if (foundAssociatedTextFinding.assocTextFinding !== 'FULL_MATCH') {
            return true;
          }
        }
      }
    
    return false;

  }  
  getAssociatedTextFinding(phraseId: number, assocPhraseId: number): any {
    return this.curRule.assocTextFindings?.sort((a, b) => {
      if (a.similarity > b.similarity) return -1;
      if (a.similarity < b.similarity) return 1;
      return 0;
    })
    .find(issue => 
      (issue.catPhraseId === phraseId && issue.assocPhraseId === assocPhraseId) ||
      (issue.catPhraseId === phraseId && issue.assocTextFinding === 'ADDED_ASSOC_TEXT')
    );
  }

  getAssociatedImageFinding(phraseId: number, assocImageId: number): any {
    return this.curRule.assocImageFindings?.sort((a, b) => {
      if (a.similarity > b.similarity) return -1;
      if (a.similarity < b.similarity) return 1;
      return 0;
    })
    .find(issue => 
      (issue.catPhraseId === phraseId && issue.assocImageId === assocImageId) ||
      (issue.catPhraseId === phraseId && issue.assocImageFinding === 'ADDED_ASSOC_IMAGE')
    );
  }

  getAddedReferenceIssue(phraseId: number): any[] {
    return this.curRule.referenceIssues?.filter(
      issue => issue.catPhraseId === phraseId && issue.refFinding === 'ADDED_REFERENCE'
    ) || [];
  }

  getMissingReferenceIssue(phraseId: number, findFootnotes: boolean = false): any[] {
    let missingReferenceIssues = this.curRule.referenceIssues?.filter(
      issue => issue.catPhraseId === phraseId && issue.refFinding === 'MISSING_REFERENCE'
    ) || [];

    return this.getMissingReferenceIssuesWithType(missingReferenceIssues, findFootnotes)
  }


  getMissingReferenceIssuesWithType(missingReferenceIssues: ReferenceIssue[], findFootnotes: boolean) {
    let filteredReferenceIssues = [];
    for (let i=0; i<missingReferenceIssues?.length; i++) {
      
      let currReferenceIssue: ReferenceIssue = missingReferenceIssues[i];
      let foundReference: ReferencePhrase = this.ruleCatMatches[this.currentIndex].catPhrase.references?.find(referenceFromCatalog => referenceFromCatalog.refPhraseId == currReferenceIssue.catRefPhraseId);
      if (foundReference) {
        if ((foundReference.refType == 'FOOTNOTE' && findFootnotes) || (foundReference.refType !== 'FOOTNOTE' && !findFootnotes)) {
          
          filteredReferenceIssues.push(currReferenceIssue);
        }
      }
    }

    return filteredReferenceIssues;
  }

  getMissingAssocTextFinding(phraseId: number): any[] {
    let result = this.curRule.assocTextFindings?.filter(
      assocFinding => assocFinding.catPhraseId === phraseId && assocFinding.assocTextFinding === 'MISSING_ASSOC_TEXT'
    ) || [];

    if (this.curRule.associatedText) {
      let usedCatAssocPhraseIds = [];

      for (let assocPhrase of this.curRule.associatedText) {
        const finding = this.getAssociatedTextFinding(phraseId, assocPhrase.assocPhraseId);
        if (finding) {
          usedCatAssocPhraseIds.push(finding.catAssocPhraseId);
        }
      }

      let nonMissingFindings = this.curRule.assocTextFindings
        ?.filter(assocFinding => assocFinding.catPhraseId === phraseId && assocFinding.assocTextFinding !== 'MISSING_ASSOC_TEXT')
        .sort((a, b) => {
          if (a.similarity > b.similarity) return -1;
          if (a.similarity < b.similarity) return 1;
          return 0;
        });

      if (nonMissingFindings) {
        for (let assocFinding of nonMissingFindings) {
          let catAssocId = assocFinding.catAssocPhraseId;
    
          if (usedCatAssocPhraseIds.indexOf(catAssocId) === -1) {
            usedCatAssocPhraseIds.push(catAssocId);
            result.push(assocFinding);
          }
        }
      }
    }

    return result;
  }

  getMissingAssocImageFindingWithType(imageFindings: AssociatedImageFinding[], findTables: boolean = false) {
    let filteredImageFindings = [];

    for (let i=0; i<imageFindings.length; i++) {
      let currImageFinding = imageFindings[i];

      let foundAssociatedImage: AssociatedImage = this.ruleCatMatches[this.currentIndex]?.catPhrase?.associatedImages?.find(associatedImage => currImageFinding.catAssocImageId == associatedImage.assocImageId);
      if (foundAssociatedImage) {
        if ((foundAssociatedImage.type == 'TABLE' && findTables) || (foundAssociatedImage.type != 'TABLE' && !findTables)) {
            
          filteredImageFindings.push(currImageFinding);
        }
      } 
    }
    return filteredImageFindings;
  }

  getMissingAssocImageFinding(phraseId: number, findTables: boolean = false): any[] {
    let result = this.curRule.assocImageFindings?.filter(
      assocFinding => (assocFinding.catPhraseId === phraseId && assocFinding.assocImageFinding === 'MISSING_ASSOC_IMAGE')
    ) || [];

    if (this.curRule.associatedImages) {
      let usedCatAssocImageIds = [];

      for (let assocImage of this.curRule.associatedImages) {
        const finding = this.getAssociatedImageFinding(phraseId, assocImage.assocImageId);
        if (finding) {
          usedCatAssocImageIds.push(finding.catAssocImageId);
        }
      }

      let nonMissingFindings = this.curRule.assocImageFindings
        ?.filter(assocFinding => assocFinding.catPhraseId === phraseId && assocFinding.assocImageFinding !== 'MISSING_ASSOC_IMAGE')
        .sort((a, b) => {
          if (a.similarity > b.similarity) return -1;
          if (a.similarity < b.similarity) return 1;
          return 0;
        });

      if (nonMissingFindings) {
        for (let assocFinding of nonMissingFindings) {
          let catAssocId = assocFinding.catAssocImageId;
    
          if (usedCatAssocImageIds.indexOf(catAssocId) === -1) {
            usedCatAssocImageIds.push(catAssocId);
            result.push(assocFinding);
          }
        }
      }
    }

    return this.getMissingAssocImageFindingWithType(result, findTables);
  }

  toggleAssociatedImages(togglingSubject: BehaviorSubject<Boolean>) {
    togglingSubject.next(!togglingSubject.getValue());
  }

  getCatPhraseReference(catRefPhraseId: number): any {
    return this.curRule.ruleCatMatches[this.currentIndex]?.catPhrase?.references?.find(
      ref => ref.refPhraseId === catRefPhraseId
    );
  }

  getCatPhraseAssocText(catAssocPhraseId: number): any {
    return this.curRule.ruleCatMatches[this.currentIndex]?.catPhrase?.associatedText?.find(
      assoc => assoc.assocPhraseId === catAssocPhraseId
    );
  }

  getCatPhraseAssocImage(catAssocImageId: number): any {
    return this.curRule.ruleCatMatches[this.currentIndex]?.catPhrase?.associatedImages?.find(
      assoc => assoc.assocImageId === catAssocImageId
    );
  }

  addToWhitelist(word: IgnoredWord) {
    this.wordsToWhitelist.push(word);
  }

  changeLoadingState(newLoadingValue: boolean) {
    
    this.loading = newLoadingValue;
  }

  showPrevRule() {
      if (this.dirtySw || this.dirtyConsistencyCheckResultsSw || this.hasWhitelistedWords()) {
      let validationStr = this.translateService.instant(
        'translate.cat.discardEditChanges'
      );

      this.sharedService.emitPopupchReceivedEvent({
        title: 'translate.validation.confirm',
        icon: 'exclamation-triangle',
        subTitle: validationStr,
        keep: false,
        approve: () => {
          this.discardChanges();
          this.showPrevRuleEmitter.emit();
        },
      });
    } else { 
      
      this.showPrevRuleEmitter.emit();
    }
  }

  showNextRule() {
    if (this.dirtySw || this.dirtyConsistencyCheckResultsSw || this.hasWhitelistedWords()) {
      let validationStr = this.translateService.instant(
        'translate.cat.discardEditChanges'
      );

      this.sharedService.emitPopupchReceivedEvent({
        title: 'translate.validation.confirm',
        icon: 'exclamation-triangle',
        subTitle: validationStr,
        keep: false,
        approve: () => {
          this.discardChanges();
          this.showNextRuleEmitter.emit();
        },
      });
    } else {
      this.showNextRuleEmitter.emit();
    }
  }

  showClaimTool() {
    this.claimToolReturnState = this.captureClaimToolReturnState();
    const setup = this.revService.createClaimToolSetup(this.curRule, this.curDoc);
    this.claimToolElement = setup.element;
    this.showClaimToolDialog = true;
    this.ngRedux.dispatch({ type: actionsList.GET_CLAIM_TOOL_ELEMENT, data: setup.element });
    this.ngRedux.dispatch({ type: actionsList.GET_CLAIM_TOOL_SAVE_CHANGES_INITIAL_VALUES, data: setup.saveChangesInitialValues });
    this.ngRedux.dispatch({ type: actionsList.SELECT_REV_RULE, data: this.curRule });
  }

  returnToDetailsFromClaimTool(): void {
    this.showClaimToolDialog = false;
    this.claimToolElement = null;
    this.claimToolReturnState = null;
  }

  onClaimToolSaved(): void {
    

    this.loading = true;
    this.showClaimToolDialog = false;
    this.revService.getRevRuleSingle(this.curDoc.docId, this.curRule.phraseId, false, false, this.curRule).pipe(takeUntil(this.destroy$)).subscribe({
      next: refreshedRule => {
        const state = this.claimToolReturnState;
        const propagateActionFromParent = this.curRule.propagateActionFromParent;
        const parentPhraseId = this.curRule.parentPhraseId;
        const isParentActionSelected = this.curRule.isParentActionSelected;
        Object.assign(this.curRule, refreshedRule);
        this.curRule.propagateActionFromParent = propagateActionFromParent;
        this.curRule.parentPhraseId = parentPhraseId;
        this.curRule.isParentActionSelected = isParentActionSelected;
        this.initialize();

        if (state?.hadUnsavedChanges) {
          this.restoreClaimToolReturnState(state);
        }
/*         if (state) {
          this.claimToolReturnStateService.restoreCatMatchReferenceOriginals(this.curRule, state.catMatchReferenceOriginals);
        } */

        
        this.claimToolElement = null;
        this.claimToolReturnState = null;
        this.loading = false;
        this.revService.recalculateRuleSections(this.curRule);
      },
      error: () => {
        this.showClaimToolDialog = false;
        this.claimToolElement = null;
        this.claimToolReturnState = null;
        this.loading = false;
        this.sharedService.showInfoMessage({ messageKey: 'translate.alert.searchError', infoType: 'error' });
      }
    });
  }

  private captureClaimToolReturnState(): RevClaimToolReturnState {
    const baseline = this.curRule.original ? JSON.parse(this.curRule.original) as Record<string, unknown> : {};
    const issues = this.claimToolReturnStateService.captureIssues(this.curRule, baseline);
    const fields: (keyof RevRule)[] = ['comment', 'ruleUserStatus', 'revStatusCode', 'contextCatPhraseId', 'referenceIssues'];
    const localValues: Record<string, unknown> = {};
    fields.forEach(field => localValues[field] = this.cloneJson(this.curRule[field]));
    return {
      hadUnsavedChanges: !!(this.dirtySw || this.dirtyConsistencyCheckResultsSw || this.hasWhitelistedWords() ||
        this.claimToolReturnStateService.hasIssueChanges(issues)),
      dirtySw: !!this.dirtySw,
      dirtyConsistencyCheckResultsSw: this.dirtyConsistencyCheckResultsSw,
      baseline,
      localValues,
      anchors: this.claimToolReturnStateService.captureAnchors(this.curRule),
      issues,
      //catMatchReferenceOriginals: this.claimToolReturnStateService.captureCatMatchReferenceOriginals(this.curRule)
    };
  }

  private restoreClaimToolReturnState(state: RevClaimToolReturnState): void {
    const rule = this.curRule as unknown as Record<string, unknown>;
    Object.entries(state.localValues).forEach(([field, localValue]) => {
      const baselineValue = state.baseline[field];
      if (!this.valuesEqual(localValue, baselineValue) && this.valuesEqual(rule[field], baselineValue)) {
        rule[field] = localValue;
      }
    });
    this.claimToolReturnStateService.restoreAnchors(this.curRule, state.anchors);
    this.revService.recalculateRuleSections(this.curRule);
    this.claimToolReturnStateService.restoreIssues(this.curRule, state.issues);
    this.dirtySw = state.dirtySw;
    this.dirtyConsistencyCheckResultsSw = state.dirtyConsistencyCheckResultsSw;
  }

  private cloneJson<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  private valuesEqual(first: unknown, second: unknown): boolean {
    return JSON.stringify(first) === JSON.stringify(second);
  }

  toggle() {
    if (this.isExpanded) {
      this.drawerDetails = null;
    }
    this.ngRedux.getState().revDocDetailsExpanded = !this.ngRedux.getState().revDocDetailsExpanded;
  }
  revertConsistencyCheckResult(catMatch: CatMatch) {
    
    this.dirtyConsistencyCheckResultsSw = true;
    let i=0;
    let consistencyIssue = null;
    let consistencyIssues = this.curRule.consistencyIssues;
    for ( ;i<consistencyIssues.length; i++) {
      consistencyIssue = consistencyIssues[i];
      if (consistencyIssue instanceof ConsistencyIssueClaim && consistencyIssue.catPhraseId == catMatch.catPhraseId) {
        break;
      }
    }
    if (i < consistencyIssues.length) {
      
      consistencyIssues.splice(i, 1);
    }
    if (consistencyIssue != null) {
      
      consistencyIssue.remove(this.curRule);
    }
  }
  viewConsistencyIssue(consistencyIssueViewModel: ConsistencyIssueViewModel) {
    
    switch(consistencyIssueViewModel.type) {
      case 'reference_consistency_issue': 
        this.currEmbeddedObjectAnnotation = consistencyIssueViewModel.data;
        this.showConsistencyIssuePhrases = true;
        break;
      case 'claim_image_consistency_issue': 
        this.drawerDetails = consistencyIssueViewModel.data;
        this.detailsDialogLabel = 'translate.consistencyIssue.details';
        this.isExpanded = true;
        break;
      case 'cat_image_consistency_issue': 
        this.drawerDetails = consistencyIssueViewModel.data?.drawerDetails;
        this.detailsDialogLabel = 'translate.consistencyIssue.details';
        this.isExpanded = true;
        break;
      case 'claim_consistency_issue':
        this.drawerDetails = consistencyIssueViewModel.data?.drawerDetails;
        this.detailsDialogLabel = 'translate.consistencyIssue.details';
        let catPhraseId = consistencyIssueViewModel.data.idToCheck;
        let correspondingIndex = this.curRule.ruleCatMatches.findIndex(catMatch => catMatch.catPhraseId == catPhraseId);
        if (correspondingIndex != -1) {
          this.currentIndex = correspondingIndex;
        }
        this.isExpanded = true;
        break;
      case 'assoc_image_consistency_issue':
            if (consistencyIssueViewModel.data.isTable) {
              this.hideAssociatedTables.next(false);
            } else {
              
              this.hideAssociatedImages.next(false);
            }
            let assocImageId = consistencyIssueViewModel.data.assocImageId;
            let foundAssocImage = this.curRule.associatedImages.find(assocImage => assocImage.assocImageId == assocImageId);
            foundAssocImage.hideConsistencyIssuesTable = false;
          setTimeout(() => {
            const target = this.assocImagesDivs.find(
              el => Number(el.nativeElement.getAttribute('data-id')) === assocImageId
            );
            if (target) {
              target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              target.nativeElement.classList.add('highlight');
              setTimeout(() => target.nativeElement.classList.remove('highlight'), 2000);
            }
          }, 1000)
    }
  }
  viewFairBalanceIssue(drawerDetails: DrawerDetails) {
    
    this.drawerDetails = drawerDetails; 
    this.detailsDialogLabel = 'translate.fairBalanceIssue.details'; 
    this.isExpanded = true;
  }
  removeIssueEvent() {
    
    this.setDirtyFlags();
  }

  onAnchorPathChange() { 

    //this.anchorsPathResultService.updateAnchors(this.curRule, anchorToUpdate);
    this.setDirtyFlags();
    
  }

  closeConsistencyCheckResultPanels() {

    if (this.disableUserActions) {
      
        this.showAddContextDropdown.value = false;
        
        this.curRule.references?.forEach((ref) => {

          this.setShowAddAnchorForReference(ref.refPhraseId, false);

        })
    }
    
  }

  setDirtyFlags() {

    this.dirtyConsistencyCheckResultsSw = this.commonService.hasIssuesToUpdate(this.curRule.consistencyIssuesToUpdate) || (this.curRule.boundingBoxAnchorsToAdd && this.curRule.boundingBoxAnchorsToAdd.length > 0) || this.checkReferences() || this.curRule.anchorsPathsToUpdate != null;
  }

  closeAllAddAnchors() {
    if (this.curRule.disableConsistencyCheckResultsActions) {
      this.showAddContextDropdown.value = false;
      
      this.curRule.references?.forEach((ref) => {
       this.setShowAddAnchorForReference(ref.refPhraseId, false); 
      })
    }
  }


  checkReferences() {

    if (this.curRule.references) {

      // checking if user added new anchor to left side
      for (let ref of this.curRule.references) {
        if (ref.isDirty()) {
          
          return true;
        }
      }
    }
    return false;

  }

    toggleAddContextDropdown() {
    this.showAddContextDropdown.value = !this.showAddContextDropdown.value;
   
  }


  toggleDisplayAnchors(ref: ReferencePhrase) {
      
    ref.showAnchors = !ref.showAnchors;
  }

  setShowAddAnchorForReference(refPhraseId: number, newValue: boolean) {

    if (this.showAddAnchorForReference[refPhraseId] == null) {
      
      this.showAddAnchorForReference[refPhraseId] = {
        value: false
      }
    }
    this.showAddAnchorForReference[refPhraseId].value = newValue;
  }

  getShowAddAnchorForReference(refPhraseId: number): { value: boolean} {
    
    if (this.showAddAnchorForReference[refPhraseId] == null) {

      this.showAddAnchorForReference[refPhraseId] = {
        value: false
      }
    }
    return this.showAddAnchorForReference[refPhraseId];
  }

  getShowAddAnchorForReferenceValue(refPhraseId: number): boolean {
    return this.showAddAnchorForReference[refPhraseId]?.value ?? false;
  }
  addedAnchor() {

    this.setDirtyFlags();
  }

  toggleVisibility(hideReferences: {toggleVisibility: Boolean}) {

    hideReferences.toggleVisibility = !hideReferences.toggleVisibility;
  }

  get footnotes() {
    
   return this.curRule.references?.filter(ref => ref.refType == 'FOOTNOTE' && !ref.isConsistencyGenerated);
  }

  get references() {

     return this.curRule.references?.filter(ref => ref.refType !== 'FOOTNOTE' && !ref.isConsistencyGenerated);

  }

  get anchorContext() {
    return this.curRule.references?.filter(ref => ref.isConsistencyGenerated);
  }

  get catMatchAnchorContext(): ReferencePhrase[] {
    if (this.curRule.ruleCatMatches.length > 0) {
      let currCatMatch = this.curRule.ruleCatMatches[this.currentIndex];
      return currCatMatch.catPhrase.references?.filter(ref => ref.isConsistencyGenerated);
    }
    return [];
  }

  get images() {
    return this.curRule.associatedImages?.filter(assocImage => assocImage.type !== 'TABLE');
  }

  get tables() {
    return this.curRule.associatedImages?.filter(assocImage => assocImage.type === 'TABLE');
  }

  viewPageAndRef(embeddedObjectAnnotation: EmbeddedObjectAnnotation) {
    this.currEmbeddedObjectAnnotation = embeddedObjectAnnotation;
    this.showConsistencyIssuePhrases = true;
  }

  get detailsDialogClass(): string {
    if (this.isExpanded) {
      return 'expanded';
    } else {
      return 'collapsed';
    }
  }
  get imageClassName() {
    return 'small-image';
  }

  get associatedImagesClassName() {
    return 'small-image';
  }

  canViewRefDoc(ref: ReferencePhrase): boolean {
    for (let refDoc of this.referenceDocuments) {
      if (refDoc.mappedReferenceTexts.includes(ref.refCatMatchTxt)) {
        return true;
      }

      for (let consistencyCheckResult of ref.anchors) {
        if (consistencyCheckResult.refDocId == refDoc.docId) {
          return true;
        }
      }
    }
    return false;
  }

  viewRefDoc(ref: ReferencePhrase) {
    for (let refDoc of this.referenceDocuments) {
      if (refDoc.mappedReferenceTexts.includes(ref.refCatMatchTxt)) {
        this.refDocToView = refDoc;
        return;
      }

      for (let consistencyCheckResult of ref.anchors) {
        if (consistencyCheckResult.refDocId == refDoc.docId) {
          this.refDocToView = refDoc;
          return;
        }
      }
    }
  }

  isAddAnchorsDisabled(): boolean {
    return getObjectPropValue(this.ngRedux.getState().uiConfig, 'anchors.add_anchors_disabled') ?? false;
  }

  getAnchorReferenceIssue(catPhraseId: number, refDocId: number): ReferencePhrase | undefined {
    return this.catMatchAnchorContext?.find(ref => ref.catPhraseId === catPhraseId && ref.refDocId === refDocId);
  }

  getMissingCatMatchAnchorContext(catMatchAnchorReference: ReferencePhrase): boolean {

    if (!this.anchorContext) {
      return true;
    }
      for (let ref of this.anchorContext) {

          if (catMatchAnchorReference.refDocId == ref.refDocId && this.ruleCatMatches[this.currentIndex].catPhrase.phraseId == catMatchAnchorReference.catPhraseId) {
            return false;
          }
        }
        if (this.curRule?.referenceIssues) {
          
          for (let refIssue of this.curRule.referenceIssues) {

            if (this.ruleCatMatches[this.currentIndex].catPhrase.phraseId == catMatchAnchorReference.catPhraseId && catMatchAnchorReference.refDocId == refIssue.refDocId && refIssue.catPhraseId == catMatchAnchorReference.catPhraseId) {
              
              return false;
            }
          }
        }

        return true;
      
      }

  getMissingAnchorReferenceIssue(catPhraseId: number, refDocId: number): ReferenceIssue | undefined {
    return this.curRule.referenceIssues?.find(ref => ref.catPhraseId === catPhraseId && ref.refDocId === refDocId && (ref.refFinding === 'MISSING_ANCHOR_REFERENCE' || ref.refFinding === 'COPIED_ANCHOR_REFERENCE'));
  }
  getBorderClass(ref: ReferencePhrase): string {

    if (!ref || ref.refType === 'FOOTNOTE') {

      return '';
    }
    if (!ref.newSw) {
      
      return ref.isConsistencyGenerated ? 'border-indicator-anchor' : 'border-indicator-reference';
    } 
    return 'border-indicator-newreference';
  }

  get conditionForDisablingConsistencyCheckResultActions() {

    return this.disableUserActions && this.curRule.disableConsistencyCheckResultsActions;
  }

  get conditionForDisablingRevStatusChanges() {
    
    return this.disableUserActions && this.dirtyConsistencyCheckResultsSw;
  }
}
